/**
 * A day and a time, as Board 11 draws them: *Mon, Oct 26* in a box and
 * *9:00 AM* in a narrower one beside it.
 *
 * On a desktop the day opens a month (Sunday first — §6.1, do-not-substitute
 * 20) under the box and the time a list of quarter hours; on a phone each is
 * the system's own picker under a 46 px box, because a finger on a native
 * wheel beats a finger on a 30 px grid (§10.2's "no native select" is about a
 * list of choices, not a date).
 */
import { useRef, useState } from 'react'
import {
  WEEKDAY_LETTERS, clock, dayComma, monthHeading, monthWeeks, stepView,
} from '@rowted/shared'
import { Popover } from '../../components/overlays.js'
import { cn } from '../../lib/cn.js'
import { Glyph } from '../glyphs.js'
import { MenuField, PICKER_LAYER, boxClass, type PickOption } from './kit.js'

export function DayField({
  value, onChange, phone, label, today, placeholder = 'Pick a day', onClear, clearLabel = 'No date', dense = false,
  sayToday = false,
}: {
  value: string | null; onChange: (value: string) => void; phone: boolean; label: string; today: string
  placeholder?: string
  /**
   * A day that may be taken away again — a task's *Due* (Aaron, Oct 25,
   * note 1.7: the Task sheet's Due is this field, as New task's is). The
   * month carries **No date** under it; a phone's own picker its Clear.
   */
  onClear?: () => void
  clearLabel?: string
  /** A phone's own picker in a desktop-sized box — the Task sheet, whose fields are all 36 px. */
  dense?: boolean
  /** Today's date reads *Today* (Board 9 E's Due box). */
  sayToday?: boolean
}) {
  const [open, setOpen] = useState(false)
  const words = (day: string) => (sayToday && day === today ? 'Today' : dayComma(day))
  const [month, setMonth] = useState(value ?? today)
  const button = useRef<HTMLButtonElement>(null)

  if (phone) {
    return (
      <span className={cn(boxClass(!dense), 'relative')}>
        <span className={cn('truncate', !value && 'text-text-faint')}>{value ? words(value) : placeholder}</span>
        <input type="date" aria-label={label} value={value ?? ''}
          onChange={(event) => {
            if (event.target.value) onChange(event.target.value)
            else onClear?.()
          }}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0" />
      </span>
    )
  }

  const monthNumber = month.slice(0, 7)
  return (
    <>
      <button ref={button} type="button" aria-label={label} aria-haspopup="dialog" aria-expanded={open}
        onClick={() => { setMonth(value ?? today); setOpen((was) => !was) }}
        className={boxClass(false, cn('flex-1 text-left', open && 'border-accent-line'))}>
        <span className={cn('truncate', !value && 'text-text-faint')}>{value ? words(value) : placeholder}</span>
      </button>
      <Popover open={open} onClose={() => setOpen(false)} anchor={button.current} label={label} width={248}
        layer={PICKER_LAYER} className="p-3">
        <div className="flex items-center gap-1.5">
          <b className="text-[13px]">{monthHeading(month)}</b>
          <span className="flex-1" />
          <button type="button" aria-label="Last month" className="text-text-muted" onClick={() => setMonth(stepView('month', month, -1))}>
            <Glyph name="chevronLeft" size={14} />
          </button>
          <button type="button" aria-label="Next month" className="text-text-muted" onClick={() => setMonth(stepView('month', month, 1))}>
            <Glyph name="chevronRight" size={14} />
          </button>
        </div>
        <div className="mt-2 grid grid-cols-7 gap-0.5">
          {WEEKDAY_LETTERS.map((letter, index) => (
            <span key={index} className="text-center text-[10px] font-semibold text-text-faint">{letter}</span>
          ))}
          {monthWeeks(month).flat().map((day) => {
            const outside = day.slice(0, 7) !== monthNumber
            return (
              <button key={day} type="button" onClick={() => { onChange(day); setOpen(false) }}
                aria-pressed={day === value}
                className={cn('h-[26px] rounded-md text-center text-[11.5px]',
                  outside ? 'text-text-faint opacity-60'
                    : day === value ? 'bg-accent font-bold text-white'
                      : day === today ? 'bg-accent-soft font-semibold text-accent-dark'
                        : day < today ? 'text-text-muted hover:bg-hover' : 'text-text-body hover:bg-hover')}>
                {Number(day.slice(8))}
              </button>
            )
          })}
        </div>
        {onClear && value && (
          <div className="mt-2 border-t border-line-faint pt-2">
            <button type="button" onClick={() => { onClear(); setOpen(false) }}
              className="text-[12.5px] font-semibold text-accent hover:underline">
              {clearLabel}
            </button>
          </div>
        )}
      </Popover>
    </>
  )
}

/** Quarter hours from 6 AM to 9 PM (the time a meeting or a timed task can take). */
const QUARTERS = Array.from({ length: (21 - 6) * 4 + 1 }, (_, n) => 6 * 60 + n * 15)

export function TimeField({
  value, onChange, phone, label, width, optional = false, dense = false,
}: {
  value: number | null; onChange: (value: number | null) => void; phone: boolean; label: string
  width?: number
  /** A task's time may be left out — *No time* is its first row. */
  optional?: boolean
  /** As `DayField`'s. */
  dense?: boolean
}) {
  if (phone) {
    const text = value === null ? '' : `${String(Math.floor(value / 60)).padStart(2, '0')}:${String(value % 60).padStart(2, '0')}`
    return (
      <span className={cn(boxClass(!dense), 'relative')}>
        <span className={cn('truncate', value === null && 'text-text-faint')}>{value === null ? 'No time' : clock(value)}</span>
        <input type="time" aria-label={label} value={text} step={900}
          onChange={(event) => {
            const [h, m] = event.target.value.split(':').map(Number)
            onChange(event.target.value ? (h ?? 0) * 60 + (m ?? 0) : null)
          }}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0" />
      </span>
    )
  }
  const options: PickOption<string>[] = [
    ...(optional ? [{ value: 'none', label: 'No time' }] : []),
    ...QUARTERS.map((minutes) => ({ value: String(minutes), label: clock(minutes) })),
  ]
  if (value !== null && !QUARTERS.includes(value)) options.push({ value: String(value), label: clock(value) })
  return (
    <MenuField label={label} phone={false} width={width} value={value === null ? (optional ? 'none' : null) : String(value)}
      options={options} onChange={(next) => onChange(next === 'none' ? null : Number(next))}
      display={<span className={cn('truncate', value === null && 'text-text-faint')}>{value === null ? 'Time' : clock(value)}</span>} />
  )
}

/**
 * A task's **Due** — Aaron, Oct 25, notes 1.7 and 1.8 — the same controls in
 * New task and on the Task sheet: the day is the month above (*a great date
 * picker*), with **No date** under it once there is one, and **a time only
 * when somebody asks for one**. A due date is a date (*There is no time
 * necessary associated with the due date, that just gets in the way*), so the
 * time box is closed until **+ Add a time** opens it — a timed task is a task
 * on somebody's calendar, and that is still a thing a person can choose (and
 * still what a task made on the calendar, or one that already has a time,
 * opens with). *Remove the time*, or picking *No time*, closes it again.
 */
export function DueControls({
  date, time, timeOn, onDate, onClear, onTime, onTimeOn, phone, today, words, placeholder, dense = false,
  sayToday = false,
}: {
  date: string | null
  time: number | null
  /** Whether the time box is open — closed by default. */
  timeOn: boolean
  onDate: (date: string) => void
  onClear: () => void
  onTime: (minutes: number | null) => void
  onTimeOn: (on: boolean) => void
  phone: boolean
  today: string
  /** The hint after the link — *a time puts it on the calendar*. */
  words?: string
  /** What an empty day says — *Pick a day* in New task, *No date* on a task. */
  placeholder?: string
  /** 36 px boxes on a phone too (the Task sheet's). */
  dense?: boolean
  /** As `DayField`'s — the Task sheet's Due reads *Today* (Board 9 E). */
  sayToday?: boolean
}) {
  const showTime = Boolean(date) && timeOn
  const link = date && (showTime ? (
    <button type="button" onClick={() => { onTime(null); onTimeOn(false) }}
      className="font-semibold text-accent hover:underline">
      Remove the time
    </button>
  ) : (
    <button type="button" onClick={() => onTimeOn(true)}
      className="inline-flex items-center gap-1 font-semibold text-accent hover:underline">
      <Glyph name="plus" size={12} />Add a time
    </button>
  ))
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5">
        <DayField label="Due" value={date} onChange={onDate} onClear={onClear} phone={phone} today={today}
          placeholder={placeholder} dense={dense} sayToday={sayToday} />
        {showTime && (
          <TimeField label="Time" value={time} phone={phone} width={phone ? undefined : 92} optional dense={dense}
            onChange={(next) => { onTime(next); if (next === null) onTimeOn(false) }} />
        )}
      </div>
      {(link || words) && (
        <div className={cn('flex flex-wrap items-center gap-x-1.5 leading-[1.4]',
          phone ? 'mt-[5px] min-h-[24px] text-[12px]' : 'mt-1 text-[11px]')}>
          {link}
          {link && words && <span aria-hidden className="text-text-faint">·</span>}
          {words && <span className="text-text-faint">{words}</span>}
        </div>
      )}
    </div>
  )
}
